/**
 * Money S5 — dashboard stránka
 *
 * Záložky: Připojení | Entity Explorer | Log
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { StatusDot } from '../../components/ui/StatusDot';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import {
  Activity,
  Banknote,
  ChevronLeft,
  ChevronRight,
  FileText,
  List,
  Plus,
  RefreshCw,
  Search,
  Server,
  Trash2,
  Zap,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Connection {
  id: string;
  label: string;
  api_url: string;
  api_version: string;
  client_id: string;
  client_secret: string;
  scope: string;
  tenant: string | null;
  enabled: boolean;
  created_at: string;
}
interface MoneyEntity {
  name: string;
  label: string;
  ops: string;
}
interface LogEntry {
  id: string;
  connection_id: string | null;
  connection_label: string | null;
  entity: string;
  operation: string;
  record_id: string | null;
  status: number;
  duration_ms: number;
  created_at: string;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MoneyS5Page() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='Money S5'
        description={t('moneyS5.pageDescription')}
        icon={<Banknote size={24} />}
      />
      <Tabs defaultTab='connections'>
        <TabList>
          <Tab id='connections' icon={<Server size={16} />}>{t('moneyS5.connections')}</Tab>
          <Tab id='explorer' icon={<Search size={16} />}>Explorer</Tab>
          <Tab id='log' icon={<Activity size={16} />}>Log</Tab>
        </TabList>
        <TabPanel id='connections' keepMounted>
          <ConnectionsTab />
        </TabPanel>
        <TabPanel id='explorer' keepMounted>
          <ExplorerTab />
        </TabPanel>
        <TabPanel id='log' keepMounted>
          <LogTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ── Connections Tab ───────────────────────────────────────────────────────────

function ConnectionsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['money-s5-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: Connection[] }>('money-s5.connections', {
        action: 'list',
      }),
    staleTime: 15_000,
  });
  const conns = data?.connections ?? [];

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.mcp('money-s5.connections', { action: 'remove', id }),
    onSuccess: () => {
      toast.success(t('moneyS5.connectionDeleted'));
      qc.invalidateQueries({ queryKey: ['money-s5-connections'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (id: string) =>
      api.mcp<{ ok: boolean; error?: string; expires_in?: number }>(
        'money-s5.connections',
        { action: 'test', id },
      ),
    onSuccess: (r) => {
      const res = r as { ok: boolean; error?: string; expires_in?: number };
      if (res.ok) toast.success(t('moneyS5.oauthOk', { seconds: res.expires_in }));
      else toast.error(t('moneyS5.oauthFailed', { error: res.error }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center justify-between'>
          <span className='flex items-center gap-2'>
            <Server size={18} /> {t('moneyS5.connections')} <Badge>{conns.length}</Badge>
          </span>
          <div className='flex gap-2'>
            <Button size='sm' variant='ghost' onClick={() => refetch()}>
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </Button>
            <Button size='sm' variant='primary' onClick={() => setShowAdd(true)}>
              <Plus size={14} /> {t('moneyS5.add')}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {conns.length === 0 && !isLoading && (
          <EmptyState icon={<Server size={32} />} title={t('moneyS5.noConnections')} />
        )}
        {conns.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b'>
                  <th className='text-left p-2'>{t('moneyS5.colStatus')}</th>
                  <th className='text-left p-2'>Label</th>
                  <th className='text-left p-2'>URL</th>
                  <th className='text-left p-2'>{t('moneyS5.colVersion')}</th>
                  <th className='text-left p-2'>Tenant</th>
                  <th className='text-right p-2'>{t('moneyS5.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {conns.map((c) => (
                  <tr key={c.id} className='border-b hover:bg-gray-50'>
                    <td className='p-2'>
                      <StatusDot status={c.enabled ? 'running' : 'stopped'} />
                    </td>
                    <td className='p-2 font-medium'>{c.label}</td>
                    <td className='p-2 font-mono text-xs truncate max-w-[250px]'>{c.api_url}</td>
                    <td className='p-2'>
                      <Badge>{c.api_version}</Badge>
                    </td>
                    <td className='p-2'>{c.tenant ?? '—'}</td>
                    <td className='p-2 text-right space-x-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => testMut.mutate(c.id)}
                        title='Test OAuth2'
                      >
                        <Zap size={14} />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() => deleteMut.mutate(c.id)}
                        title={t('moneyS5.delete')}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {showAdd && <AddConnectionModal onClose={() => setShowAdd(false)} />}
    </Card>
  );
}

function AddConnectionModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    label: '',
    api_url: '',
    api_version: 'v2.0',
    client_id: '',
    client_secret: '',
    scope: 'S5Api',
    tenant: '',
  });
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const addMut = useMutation({
    mutationFn: () => api.mcp('money-s5.connections', { action: 'add', ...form }),
    onSuccess: () => {
      toast.success(t('moneyS5.connectionCreated'));
      qc.invalidateQueries({ queryKey: ['money-s5-connections'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal open onClose={onClose} title={t('moneyS5.newConnectionTitle')} size='lg'>
      <div className='space-y-3'>
        <div>
          <Label>Label *</Label>
          <Input
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder={t('moneyS5.labelPlaceholder')}
          />
        </div>
        <div>
          <Label>API URL *</Label>
          <Input
            value={form.api_url}
            onChange={(e) => set('api_url', e.target.value)}
            placeholder='https://money.firma.cz'
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <Label>{t('moneyS5.apiVersionLabel')}</Label>
            <Select
              className='w-full'
              value={form.api_version}
              onChange={(e) => set('api_version', e.target.value)}
            >
              <option value='v2.0'>v2.0</option>
              <option value='v1.0'>v1.0</option>
            </Select>
          </div>
          <div>
            <Label>Scope</Label>
            <Input value={form.scope} onChange={(e) => set('scope', e.target.value)} />
          </div>
        </div>
        <div>
          <Label>Client ID *</Label>
          <Input value={form.client_id} onChange={(e) => set('client_id', e.target.value)} />
        </div>
        <div>
          <Label>Client Secret *</Label>
          <Input
            type='password'
            value={form.client_secret}
            onChange={(e) => set('client_secret', e.target.value)}
          />
        </div>
        <div>
          <Label>{t('moneyS5.tenantOptionalLabel')}</Label>
          <Input
            value={form.tenant}
            onChange={(e) => set('tenant', e.target.value)}
            placeholder={t('moneyS5.tenantPlaceholder')}
          />
        </div>
        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='secondary' onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            onClick={() => addMut.mutate()}
            disabled={!form.label || !form.api_url || !form.client_id || !form.client_secret}
          >
            {t('moneyS5.create')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Explorer Tab ──────────────────────────────────────────────────────────────

function ExplorerTab() {
  const { t } = useTranslation();
  const [selectedConn, setSelectedConn] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const { data: connsData } = useQuery({
    queryKey: ['money-s5-connections'],
    queryFn: () =>
      api.mcp<{ ok: boolean; connections?: Connection[] }>('money-s5.connections', {
        action: 'list',
      }),
    staleTime: 15_000,
  });
  const conns = (connsData?.connections ?? []).filter((c) => c.enabled);

  const { data: entitiesData } = useQuery({
    queryKey: ['money-s5-entities'],
    queryFn: () =>
      api.mcp<{ ok: boolean; entities?: MoneyEntity[] }>('money-s5.list_entities'),
    staleTime: 300_000,
  });
  const entities = entitiesData?.entities ?? [];

  const { data: queryData, isLoading, refetch } = useQuery({
    queryKey: ['money-s5-query', selectedConn, selectedEntity, offset, filter],
    queryFn: () =>
      api.mcp<
        { ok: boolean; data?: Record<string, unknown>[]; count?: number; error?: string }
      >('money-s5.query', {
        entity: selectedEntity,
        offset,
        limit,
        ...(selectedConn ? { connection_id: selectedConn } : {}),
        ...(filter ? { filter } : {}),
      }),
    enabled: !!selectedEntity,
    staleTime: 15_000,
  });

  const rows = queryData?.data ?? [];
  const columns = rows.length > 0 ? Object.keys(rows[0]).slice(0, 8) : [];

  return (
    <div className='space-y-4'>
      {/* Connection selector */}
      {conns.length > 1 && (
        <Card>
          <CardContent className='py-3 px-4'>
            <Label className='mr-2'>{t('moneyS5.connectionLabel')}</Label>
            <Select
              value={selectedConn}
              onChange={(e) => {
                setSelectedConn(e.target.value);
                setOffset(0);
              }}
            >
              <option value=''>{t('moneyS5.defaultOption')}</option>
              {conns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Entity selector */}
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <List size={18} /> {t('moneyS5.entityTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='flex flex-wrap gap-2'>
            {entities.map((e) => (
              <Button
                key={e.name}
                size='sm'
                variant={selectedEntity === e.name ? 'primary' : 'secondary'}
                onClick={() => {
                  setSelectedEntity(e.name);
                  setOffset(0);
                }}
              >
                {e.label} <Badge className='ml-1 text-xs'>{e.ops}</Badge>
              </Button>
            ))}
          </div>
          {entities.length === 0 && (
            <EmptyState icon={<Server size={32} />} title={t('moneyS5.loadingEntities')} />
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {selectedEntity && (
        <Card>
          <CardHeader>
            <CardTitle className='flex items-center justify-between'>
              <span className='flex items-center gap-2'>
                <FileText size={18} /> {selectedEntity}{' '}
                {queryData?.count !== undefined && <Badge>{queryData.count}</Badge>}
              </span>
              <Button size='sm' variant='ghost' onClick={() => refetch()}>
                <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-3'>
            <div className='flex gap-2'>
              <Input
                className='flex-1'
                placeholder={t('moneyS5.filterPlaceholder')}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOffset(0);
                    refetch();
                  }
                }}
              />
              <Button
                size='sm'
                variant='primary'
                onClick={() => {
                  setOffset(0);
                  refetch();
                }}
              >
                <Search size={14} /> {t('moneyS5.search')}
              </Button>
            </div>
            {queryData && !queryData.ok && (
              <div className='p-3 bg-red-50 text-red-700 rounded text-sm'>{queryData.error}</div>
            )}
            {rows.length > 0 && (
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b'>
                      {columns.map((c) => (
                        <th key={c} className='text-left p-2 font-medium text-gray-600'>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className='border-b hover:bg-gray-50'>
                        {columns.map((c) => (
                          <td
                            key={c}
                            className='p-2 truncate max-w-[200px]'
                            title={String(row[c] ?? '')}
                          >
                            {fmt(row[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!isLoading && rows.length === 0 && queryData?.ok && (
              <EmptyState icon={<FileText size={32} />} title={t('moneyS5.noRecords')} />
            )}
            {rows.length > 0 && (
              <div className='flex items-center justify-between pt-2'>
                <span className='text-sm text-gray-500'>
                  {t('moneyS5.offsetShown', { offset, shown: rows.length })}
                </span>
                <div className='flex gap-2'>
                  <Button
                    size='sm'
                    variant='ghost'
                    disabled={offset === 0}
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                  >
                    <ChevronLeft size={14} /> {t('moneyS5.previous')}
                  </Button>
                  <Button
                    size='sm'
                    variant='ghost'
                    disabled={rows.length < limit}
                    onClick={() => setOffset(offset + limit)}
                  >
                    {t('moneyS5.next')} <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Log Tab ───────────────────────────────────────────────────────────────────

function LogTab() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['money-s5-log'],
    queryFn: () => api.mcp<{ ok: boolean; log?: LogEntry[] }>('money-s5.log', { limit: 100 }),
    staleTime: 10_000,
  });
  const entries = data?.log ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center justify-between'>
          <span className='flex items-center gap-2'>
            <Activity size={18} /> Audit Log <Badge>{entries.length}</Badge>
          </span>
          <Button size='sm' variant='ghost' onClick={() => refetch()}>
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {entries.length === 0 && !isLoading && (
          <EmptyState icon={<Activity size={32} />} title={t('moneyS5.noCallsYet')} />
        )}
        {entries.length > 0 && (
          <div className='overflow-x-auto'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b'>
                  <th className='text-left p-2'>{t('moneyS5.colTime')}</th>
                  <th className='text-left p-2'>{t('moneyS5.colConnection')}</th>
                  <th className='text-left p-2'>{t('moneyS5.colEntity')}</th>
                  <th className='text-left p-2'>{t('moneyS5.colOperation')}</th>
                  <th className='text-left p-2'>ID</th>
                  <th className='text-left p-2'>Status</th>
                  <th className='text-right p-2'>ms</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className='border-b hover:bg-gray-50'>
                    <td className='p-2 text-gray-500 whitespace-nowrap'>
                      {new Date(e.created_at).toLocaleString('cs')}
                    </td>
                    <td className='p-2 text-xs'>{e.connection_label ?? '—'}</td>
                    <td className='p-2 font-mono'>{e.entity}</td>
                    <td className='p-2'>
                      <Badge variant={opVar(e.operation)}>{e.operation}</Badge>
                    </td>
                    <td className='p-2 font-mono text-xs truncate max-w-[120px]'>
                      {e.record_id ?? '—'}
                    </td>
                    <td className='p-2'>
                      <StatusDot status={e.status < 400 ? 'running' : 'error'} /> {e.status}
                    </td>
                    <td className='p-2 text-right font-mono'>{e.duration_ms}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? i18n.t('moneyS5.yes') : i18n.t('moneyS5.no');
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
  return String(v);
}
function opVar(op: string): 'success' | 'warning' | 'error' | 'secondary' {
  switch (op) {
    case 'create':
      return 'success';
    case 'update':
      return 'warning';
    case 'delete':
      return 'error';
    default:
      return 'secondary';
  }
}
